import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useOnline } from '@hozana/hooks/useOnline'
import { usePrevious } from '@hozana/hooks/usePrevious'
import { useTranslate } from '@hozana/intl/useTranslate'

import { Button } from 'elements/button/Button'
import { Block } from 'elements/layout/Block'
import { Div } from 'elements/layout/Div'
import { FixedDiv } from 'elements/layout/FixedDiv'
import { Space } from 'elements/layout/Space'
import { Loading } from 'elements/ui/Loading'

import type { TQueryDetails } from './hooks/useApiData'

type TApiHandlerProps = {
  /** One or more queries we want to check */
  queries: TQueryDetails[]
  /** If not set, will display the error/loading in a simple div */
  display?: 'fullPage' | 'block'
  /** Function called on network errors. If not set, will try to refetch all queries with errors */
  onRetry?: () => void
  noLoadingDisplay?: boolean
}

/**
 * ### ApiHandler ###
 *
 * This component can be used to handle network failures of any component receiving the result of queries.
 *
 * It will display a block that indicates the network failure and a button to retry the requests. You must pass an array of queries that you want to listen to, with `queries`.
 *
 * You can pass the other queries that you want to refetch, with `onRetry`.
 * You can choose to display the error on the full page or simply in a block with `display`.
 *
 * ```js
 * <ApiHandler
 *   queries={[community]}
 *   display="fullPage"
 *   onRetry={() => {
 *     community.refetch();;
 *   }}
 * />
 * ```
 */
export const ApiHandler: TFCC<TApiHandlerProps> = ({ children, queries, display, onRetry, noLoadingDisplay }) => {
  const online = useOnline()
  const prevOnline = usePrevious(online)
  const { t } = useTranslation()

  const isNetworkError = (errorMessage: string) =>
    errorMessage?.endsWith('common.error.network-failure.short') ||
    errorMessage?.endsWith('common.error.network-failure.long')

  const retry = useCallback(() => {
    if (onRetry) {
      onRetry()
    } else {
      // Refetch all queries with network errors
      queries.forEach((query) => {
        if (isNetworkError(query.error)) {
          query.refetch()
        }
      })
    }
  }, [onRetry, queries])

  useEffect(() => {
    // Detect if we have one or more network error
    // If we detect a reconnexion and we have one or more network error, automatically retry
    if (!prevOnline && online && queries.some((query) => isNetworkError(query.error))) {
      retry()
    }
  }, [online, prevOnline, queries, retry])

  let error = ''
  let loading = false

  if (queries) {
    queries.some((query) => {
      if (!query || query.loading) {
        loading = true
      } else {
        // Keep in memory only the first error message
        if (query.error && !error) error = query.error
      }

      // Break as soon as we detect a query is still loading
      return loading
    })
  } else {
    loading = true
  }

  let cleanedI18nError = error.replace('trans:', '')
  if (cleanedI18nError.match(/^(?!.*:).*$/)) {
    //namespace not provided
    cleanedI18nError = `common:${cleanedI18nError}`
  }
  const i18nError = useTranslate(cleanedI18nError)

  // If all queries have loaded and have no error,
  // Render children if there is one, otherwise display nothing
  if (!error && (!loading || noLoadingDisplay)) {
    return <>{children}</>
  }

  const content = loading ? (
    // If one or more query is still loading, wait for it
    <Loading />
  ) : (
    // If there is an error, display the first error message
    <Div align="center" p="1em">
      {i18nError}
      {error?.endsWith('common.error.network-failure.long') && <div>{t('common:common.error.page-not-available')}</div>}
      <Space />
      {(error?.endsWith('common.error.network-failure.short') ||
        error?.endsWith('common.error.network-failure.long')) && (
        <Button colors="greyLight" onClick={retry}>
          trans:common:word.retry
        </Button>
      )}
    </Div>
  )

  // Add wrapper

  if (display === 'fullPage') {
    return (
      <FixedDiv top bottom left right align="middleCenter" bg="background" zIndex="error">
        {content}
      </FixedDiv>
    )
  }

  if (display === 'block') {
    if (error) {
      return <Block m="0 0 1em">{content}</Block>
    }
    // Same height as Block so that we don't get flickering display
    return (
      <Div h="105px" m="0 0 1em">
        {content}
      </Div>
    )
  }

  return content
}
